import { skipToken, useQuery } from '@tanstack/react-query';
import { classes } from 'html-classes';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ETAPaymentMethod } from '~/api/ETA';
import type { Order, OrderShort } from '~/api/Order';
import Icon from '~/components/Icon/Icon';
import { useReorder } from '~/hooks/useReorder';
import { notificationService } from '~/services/NotificationService';
import { checkoutStore, orderStore } from '~/stores';

import { PaymentError } from './components/PaymentError/PaymentError';
import { PaymentInProgress } from './components/PaymentInProgress/PaymentInProgress';
import { PaymentSuccess } from './components/PaymentSuccess/PaymentSuccess';
import { QRCodePayment } from './components/QRCodePayment/QRCodePayment';
import styles from './OrderPayment.module.scss';
import { getDeadlineMs, LOW_TIME_THRESHOLD_MS } from './utils';

const Layout: FC<{ children: ReactNode; qr?: boolean; centered?: boolean }> = ({
  children,
  qr,
  centered,
}) => (
  <div className={styles.orderPaymentPage}>
    <div
      className={classes([
        styles.orderPaymentPageContent,
        {
          [styles.orderPaymentPageContentQR!]: qr,
          [styles.orderPaymentPageContentCentered!]: centered,
        },
      ])}
    >
      {children}
    </div>
  </div>
);

export const OrderPaymentPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [now, setNow] = useState(() => Date.now());

  const {
    data: order,
    isLoading,
    isError,
    error,
  } = useQuery<Order | OrderShort | null>({
    queryKey: ['order', orderId],
    queryFn: orderId ? () => orderStore.requestOrder(orderId) : skipToken,
    enabled: !!orderId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || (data.status !== 'created' && data.status !== 'reserved')) {
        return false;
      }

      const deadline = getDeadlineMs(data.cancel_at);
      if (deadline <= 0 || deadline - Date.now() <= 0) {
        return false;
      }

      return 5000;
    },
  });

  const deadlineMs =
    order && (order.status === 'created' || order.status === 'reserved')
      ? getDeadlineMs(order.cancel_at)
      : 0;
  const remainingMs = deadlineMs - now;

  const { handleReorder, isReorderLoading } = useReorder(order, {
    skipNavigation: true,
  });

  const isPaymentInProgress =
    order?.status === 'created' || order?.status === 'reserved';
  const isQRPayment = order?.payment.payment_method === ETAPaymentMethod.QR;
  const isQRPaymentFromSearchParams =
    searchParams.get('payment_method') === ETAPaymentMethod.QR;
  const isTimedOut = deadlineMs > 0 && remainingMs <= 0 && isPaymentInProgress;
  const isLowTime =
    remainingMs > 0 &&
    remainingMs <= LOW_TIME_THRESHOLD_MS &&
    isPaymentInProgress;

  const lowTimeNotifiedRef = useRef(false);

  useEffect(() => {
    if (isLowTime && !lowTimeNotifiedRef.current) {
      lowTimeNotifiedRef.current = true;
      notificationService.warning(
        t('paymentLowTimeWarningTitle'),
        t('paymentLowTimeWarning'),
      );
    }
  }, [isLowTime, t]);

  const handleCancelAndReturn = useCallback(async () => {
    if (orderId) {
      await orderStore.orderCancel(orderId);
    }
    await handleReorder();
    notificationService.error(
      t('paymentCancelled'),
      t('paymentCancelledDescription'),
    );
    navigate('/checkout', { replace: true });
  }, [orderId, handleReorder, navigate, t]);

  useEffect(() => {
    orderStore.setOnBackFromPayment(handleCancelAndReturn);
    return () => orderStore.setOnBackFromPayment(null);
  }, [handleCancelAndReturn]);

  useEffect(() => {
    if (!order || (order.status !== 'created' && order.status !== 'reserved')) {
      return;
    }

    const intervalId = setInterval(() => setNow(Date.now()), 1000);

    if (!['pvit', 'octo'].includes(order.payment.payment_system)) {
      navigate(`/order/${orderId}`, { replace: true });
    }

    return () => clearInterval(intervalId);
  }, [order, navigate, orderId]);

  useEffect(() => {
    if (
      order &&
      orderId &&
      !isPaymentInProgress &&
      order.status !== 'cancelled'
    ) {
      checkoutStore.onPaymentSuccess(orderId);
    }
  }, [order, isPaymentInProgress, orderId]);

  useEffect(() => {
    if (order && !isPaymentInProgress && order.status !== 'cancelled') {
      setSearchParams(
        (prev) => {
          prev.set('status', 'success');
          return prev;
        },
        { replace: true },
      );
    }
  }, [order, isPaymentInProgress, setSearchParams]);

  if (isLoading) {
    return (
      <Layout qr={isQRPayment || isQRPaymentFromSearchParams} centered>
        {isQRPayment || isQRPaymentFromSearchParams ? (
          <QRCodePayment remainingMs={0} isLowTime={false} />
        ) : (
          <>
            <Icon type="timer" size={48} className={styles.timerIcon} />
            <p>{t('loading')}</p>
          </>
        )}
      </Layout>
    );
  }

  if (isError || !order) {
    return (
      <Layout>
        <p>{error instanceof Error ? error.message : t('errors:unknown')}</p>
      </Layout>
    );
  }

  if (isTimedOut) {
    return (
      <Layout>
        <PaymentError
          description={t('paymentTimedOutDescription')}
          onAction={handleCancelAndReturn}
          isActionLoading={isReorderLoading}
        />
      </Layout>
    );
  }

  if (isPaymentInProgress && isQRPayment) {
    return (
      <Layout qr>
        <QRCodePayment
          qrCodeValue={order.payment.payment_metadata.qrCode}
          remainingMs={remainingMs}
          isLowTime={isLowTime}
        />
      </Layout>
    );
  }

  if (isPaymentInProgress) {
    return (
      <Layout centered>
        <PaymentInProgress remainingMs={remainingMs} isLowTime={isLowTime} />
      </Layout>
    );
  }

  if (order.status === 'cancelled') {
    return (
      <Layout>
        <PaymentError
          description={t('paymentNotConfirmedDescription')}
          onAction={handleCancelAndReturn}
          isActionLoading={isReorderLoading}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <PaymentSuccess orderId={orderId ?? ''} />
    </Layout>
  );
};
